document.documentElement.classList.remove('no-js');

function initRevealMotion(scope = document) {
  if (!document.body.classList.contains('pi-motion-on') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const targets = [...scope.querySelectorAll('#MainContent .shopify-section, #MainContent .pi-grid > .pi-card, #MainContent .pi-studio-tile')];
  if (!('IntersectionObserver' in window)) { targets.forEach((target) => target.classList.add('is-visible')); return; }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px' });
  targets.forEach((target, index) => {
    if (target.classList.contains('pi-reveal')) return;
    target.classList.add('pi-reveal');
    target.style.setProperty('--pi-reveal-delay', `${(index % 5) * 55}ms`);
    observer.observe(target);
  });
  document.body.classList.add('pi-motion-ready');
}

initRevealMotion();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.pi-reveal').forEach((target) => target.classList.add('is-visible'));
});

class PiSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-slide-dot]')];
    this.prevButton = this.querySelector('[data-slide-prev]');
    this.nextButton = this.querySelector('[data-slide-next]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.delay = Number(this.dataset.delay || 6500);
    this.autoplay = this.dataset.autoplay === 'true';
    this.prevButton?.addEventListener('click', () => this.show(this.index - 1, true));
    this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.querySelectorAll('[data-video-toggle]').forEach((button) => button.addEventListener('click', () => this.toggleVideo(button)));
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1, true);
      if (event.key === 'ArrowRight') this.show(this.index + 1, true);
    });
    this.syncVideos();
    this.start();
  }
  show(nextIndex, restart = false) {
    if (this.slides.length < 2) return;
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.index);
      dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
    });
    this.syncVideos();
    if (restart) { this.stop(); this.start(); }
  }
  syncVideos() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slides.forEach((slide, index) => {
      slide.querySelectorAll('video').forEach((video) => {
        if (!reduceMotion && index === this.index && slide.dataset.videoPaused !== 'true') video.play().catch(() => {});
        else video.pause();
      });
    });
  }
  toggleVideo(button) {
    const slide = button.closest('[data-slide]');
    const willPause = slide.dataset.videoPaused !== 'true';
    slide.dataset.videoPaused = willPause ? 'true' : 'false';
    slide.querySelectorAll('video').forEach((video) => willPause ? video.pause() : video.play().catch(() => {}));
    button.setAttribute('aria-pressed', willPause ? 'true' : 'false');
    button.textContent = willPause ? 'Play video' : 'Pause video';
  }
  start() {
    if (!this.autoplay || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
  }
  stop() { if (this.timer) window.clearInterval(this.timer); }
}

customElements.define('pi-slider', PiSlider);

function showProductMedia(gallery, mediaId) {
  if (!gallery || !mediaId) return;
  const mediaItems = [...gallery.querySelectorAll('.pi-product__media--stage')];
  const requestedMedia = mediaItems.find((item) => item.dataset.productMedia === String(mediaId));
  if (!requestedMedia) return;

  mediaItems.forEach((item) => {
    const active = item === requestedMedia;
    item.hidden = !active;
    item.classList.toggle('is-hidden', !active);
    item.classList.remove('is-zoomed');
    if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
  });

  gallery.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
    const active = thumbnail.dataset.productThumbnail === String(mediaId);
    thumbnail.classList.toggle('is-active', active);
    thumbnail.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

document.addEventListener('click', (event) => {
  const productThumbnail = event.target.closest('[data-product-thumbnail]');
  if (productThumbnail) {
    showProductMedia(productThumbnail.closest('[data-product-gallery]'), productThumbnail.dataset.productThumbnail);
  }
  const inlineVideoToggle = event.target.closest('[data-inline-video-toggle]');
  if (inlineVideoToggle) {
    const container = inlineVideoToggle.closest('.pi-anywhere__shell, .pi-image-text__media, .pi-card-media, .pi-studio-tile');
    const videos = [...(container?.querySelectorAll('video') || [])].filter((video) => getComputedStyle(video).display !== 'none');
    const willPause = videos.some((video) => !video.paused);
    videos.forEach((video) => willPause ? video.pause() : video.play().catch(() => {}));
    inlineVideoToggle.setAttribute('aria-pressed', willPause ? 'true' : 'false');
    inlineVideoToggle.textContent = willPause ? 'Play video' : 'Pause video';
  }
  const pageVideoToggle = event.target.closest('[data-page-video-toggle]');
  if (pageVideoToggle) {
    const banner = pageVideoToggle.closest('.pi-banner');
    const video = banner?.querySelector('video');
    if (video) {
      const willPause = !video.paused;
      if (willPause) video.pause(); else video.play().catch(() => {});
      pageVideoToggle.setAttribute('aria-pressed', willPause ? 'true' : 'false');
      pageVideoToggle.textContent = willPause ? 'Play video' : 'Pause video';
    }
  }
  const toggle = event.target.closest('[data-menu-toggle]');
  if (toggle) {
    const nav = document.querySelector('[data-mobile-menu]');
    const open = nav?.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  const accordion = event.target.closest('[data-accordion-button]');
  if (accordion) {
    const item = accordion.closest('.pi-accordion__item');
    const open = item.classList.toggle('is-open');
    accordion.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
});

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-variant-select]');
  if (!select) return;
  const option = select.options[select.selectedIndex];
  const form = select.closest('form');
  const idInput = form?.querySelector('input[name="id"]');
  if (idInput) idInput.value = option.value;
  const button = form?.querySelector('[data-add-to-cart]');
  if (button) {
    const available = option.dataset.available === 'true';
    button.disabled = !available;
    button.textContent = available ? button.dataset.availableText : button.dataset.soldText;
  }
  if (option.dataset.featuredMediaId) {
    showProductMedia(form?.closest('.pi-product')?.querySelector('[data-product-gallery]'), option.dataset.featuredMediaId);
  }
});

if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.querySelectorAll('.pi-slide video, .pi-banner video, .pi-anywhere video, .pi-image-text video, .pi-card-media video, .pi-category-card video').forEach((video) => video.pause());
  document.querySelectorAll('[data-video-toggle], [data-page-video-toggle], [data-inline-video-toggle]').forEach((button) => {
    button.setAttribute('aria-pressed', 'true');
    button.textContent = 'Play video';
  });
}

/* Product image magnifier: follows the pointer without changing gallery layout. */
if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.querySelectorAll('.pi-product--zoom-enabled .pi-product__media--stage').forEach((media) => {
    const productImage = media.querySelector('img');
    if (!productImage) return;

    media.addEventListener('pointerenter', () => {
      media.classList.add('is-zoomed');
    });

    media.addEventListener('pointermove', (event) => {
      const bounds = media.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - bounds.left) / bounds.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - bounds.top) / bounds.height) * 100));
      media.style.setProperty('--pi-zoom-x', `${x}%`);
      media.style.setProperty('--pi-zoom-y', `${y}%`);
    });

    media.addEventListener('pointerleave', () => {
      media.classList.remove('is-zoomed');
      media.style.removeProperty('--pi-zoom-x');
      media.style.removeProperty('--pi-zoom-y');
    });
  });
}
